import { arrayOf, bool, func, number, string } from 'prop-types';
import React from 'react';
import { Label } from 'reactstrap';

import Association from '../../associations/Association';
import { brandClusterOptions } from '../../brands/constants';
import { filterStatuses as campaignStatusOptions } from '../../campaigns/constants';
import { businessOptions } from '../../constants';
import DateRange from '../../generic/DateRange';
import MultiSelect from '../../generic/MultiSelect';
import { getTooltipLabelWithText } from '../../generic/Tooltip';
import {
  businessModels as businessModelOptions,
  campaignObjectiveOptions,
  publicStages as opportunityStageOptions,
  salesUnits as salesUnitOptions,
} from '../../opportunities/constants';
import { selfServiceActivatedOptions } from '../constants';
import HELP_TEXTS from '../help-texts';

const TooltipLabel = getTooltipLabelWithText(HELP_TEXTS);

/* eslint-disable camelcase */

function ContactFilters({
  onChange,
  readOnly,
  brandId,
  brandCluster,
  opportunitySalesUnit,
  opportunityBusinessModel,
  opportunityCampaignObjective,
  opportunityCommercialPackageId,
  productBusiness,
  campaignStartDate_after,
  campaignStartDate_before,
  campaignEndDate_after,
  campaignEndDate_before,
  opportunityStage,
  campaignStatus,
  selfServiceActivated,
}) {
  const inputProps = { onChange, readOnly };
  return (
    <>
      <TooltipLabel
        helpKey="contactFilters"
        text="Filters"
        section="labels"
        resource="cf"
      />
      <div className="small">
        <div className="mb-2">
          <Label for="cf-brandId" className="small-1">
            Brands
          </Label>
          <Association
            id="cf-brandId"
            name="brandId"
            type="brand"
            value={brandId}
            isMulti
            disableInactiveView
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-brandCluster" className="small-1">
            Brand clusters
          </Label>
          <MultiSelect
            id="cf-brandCluster"
            name="brandCluster"
            options={brandClusterOptions}
            value={brandCluster}
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-opportunitySalesUnit" className="small-1">
            Sales Units
          </Label>
          <MultiSelect
            id="cf-opportunitySalesUnit"
            name="opportunitySalesUnit"
            options={salesUnitOptions}
            value={opportunitySalesUnit}
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-opportunityBusinessModel" className="small-1">
            Business model
          </Label>
          <MultiSelect
            id="cf-opportunityBusinessModel"
            name="opportunityBusinessModel"
            options={businessModelOptions}
            value={opportunityBusinessModel}
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-opportunityCampaignObjective" className="small-1">
            Campaign objective
          </Label>
          <MultiSelect
            id="cf-opportunityCampaignObjective"
            name="opportunityCampaignObjective"
            options={campaignObjectiveOptions}
            value={opportunityCampaignObjective}
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-opportunityCommercialPackageId" className="small-1">
            Commercial package
          </Label>
          <Association
            id="cf-opportunityCommercialPackageId"
            name="opportunityCommercialPackageId"
            value={opportunityCommercialPackageId}
            type="commercialPackage"
            isMulti
            disableInactiveView
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-productBusiness" className="small-1">
            Business
          </Label>
          <MultiSelect
            id="cf-productBusiness"
            name="productBusiness"
            options={businessOptions}
            value={productBusiness}
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-campaignStartDates" className="small-1">
            Planned Start Date
          </Label>
          <DateRange
            id="cf-campaignStartDates"
            endName="campaignStartDate_before"
            startName="campaignStartDate_after"
            endDate={campaignStartDate_before}
            startDate={campaignStartDate_after}
            small
            openDirection="up"
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-campaignEndDates" className="small-1">
            Planned End Date
          </Label>
          <DateRange
            id="cf-campaignEndDates"
            endName="campaignEndDate_before"
            startName="campaignEndDate_after"
            endDate={campaignEndDate_before}
            startDate={campaignEndDate_after}
            small
            openDirection="up"
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-opportunityStage" className="small-1">
            Opportunity stage
          </Label>
          <MultiSelect
            id="cf-opportunityStage"
            name="opportunityStage"
            options={opportunityStageOptions}
            value={opportunityStage}
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-campaignStatus" className="small-1">
            Campaign status
          </Label>
          <MultiSelect
            id="cf-campaignStatus"
            name="campaignStatus"
            options={campaignStatusOptions}
            value={campaignStatus}
            {...inputProps}
          />
        </div>
        <div className="mb-2">
          <Label for="cf-selfServiceActivated" className="small-1">
            Self Service Activated
          </Label>
          <MultiSelect
            id="cf-selfServiceActivated"
            name="selfServiceActivated"
            options={selfServiceActivatedOptions}
            value={selfServiceActivated}
            {...inputProps}
          />
        </div>
      </div>
    </>
  );
}

ContactFilters.propTypes = {
  onChange: func.isRequired,
  readOnly: bool,
  brandId: arrayOf(number.isRequired),
  brandCluster: arrayOf(string.isRequired),
  opportunitySalesUnit: arrayOf(string.isRequired),
  opportunityBusinessModel: arrayOf(string.isRequired),
  opportunityCampaignObjective: arrayOf(string.isRequired),
  opportunityCommercialPackageId: arrayOf(number.isRequired),
  opportunityStage: arrayOf(string.isRequired),
  productBusiness: arrayOf(string.isRequired),
  campaignStartDate_after: string,
  campaignStartDate_before: string,
  campaignEndDate_after: string,
  campaignEndDate_before: string,
  campaignStatus: arrayOf(string.isRequired),
  selfServiceActivated: arrayOf(string.isRequired),
};

ContactFilters.defaultProps = {
  readOnly: false,
  brandId: [],
  brandCluster: [],
  opportunitySalesUnit: [],
  opportunityBusinessModel: [],
  opportunityCampaignObjective: [],
  opportunityCommercialPackageId: [],
  opportunityStage: [],
  productBusiness: [],
  campaignStartDate_after: null,
  campaignStartDate_before: null,
  campaignEndDate_after: null,
  campaignEndDate_before: null,
  campaignStatus: [],
  selfServiceActivated: [],
};

export default ContactFilters;
